/**
 * Public API client — POSTs to the cycloud-public-api Worker.
 *
 * On any failure (network, timeout > 3s, 5xx, 429), callers fall back
 * to canonical hardcoded values from sizing-fallback.ts.
 */

export interface PublicSizeResponse {
  recommendation: {
    sku: string
    nodes: number
    configSummary: string
    limitFactor: 'cpu' | 'memory' | 'storage'
  }
  pricing: {
    monthlyLow: number
    monthlyHigh: number
    currency: 'USD'
    termMonths: number
    tcvLow: number
    tcvHigh: number
  }
  comparison: {
    cyCloud: { tcvLow: number; tcvHigh: number }
    awsGovCloud: { tcvLow: number; tcvHigh: number }
    azureGov: { tcvLow: number; tcvHigh: number }
    diyFederal: { tcvLow: number; tcvHigh: number }
  }
  methodology: {
    discountRatePct: number
    refreshReserveAnnualPct: number
    perCategoryEscalation: {
      compute: number
      power: number
      labor: number
      colo: number
    }
    stigComplianceAnnual: 'included' | 'excluded'
    fteAllocationBand: string
    hyperscalerComparison: {
      comparisonBasket: string[]
      awsGovCloud: { representativeFamily: string; region: string; commitmentPosture: string }
      azureGov: { representativeFamily: string; region: string; commitmentPosture: string }
    }
    comparisonConstructedDate: string
  }
  deliveryLeadTimeWeeks: number
  disclaimer: string
  needsArchitectureReview: boolean
}

export interface SizeByWorkloadInput {
  vmCount: number
  avgVcpu: number
  avgRamGb: number
  avgVmStorageGb: number
  termMonths: 12 | 36 | 60
}

export type SpendProvider = 'aws_govcloud' | 'azure_gov' | 'gcc_high' | 'other_diy'

export interface SizeBySpendInput {
  currentMonthlySpend: number
  currentProvider: SpendProvider
  termMonths: 12 | 36 | 60
}

export interface LeadInput {
  name: string
  email: string
  organization: string
  role: string
  inquiryType:
    | 'capture_teaming'
    | 'architecture_review'
    | 'capability_statement'
    | 'general'
  message: string
  honeypot: string
}

export interface LeadResponse {
  success: boolean
  message?: string
  error?: string
  downloadUrl?: string
}

const API_BASE = (import.meta.env.VITE_PUBLIC_API_BASE_URL as string | undefined) ??
  'https://cycloud-public-api.redskiesmourning.workers.dev'

const TIMEOUT_MS = 3000

async function postWithTimeout<T>(path: string, body: unknown): Promise<T> {
  const controller = new AbortController()
  const id = setTimeout(() => controller.abort(), TIMEOUT_MS)
  try {
    const resp = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    })
    if (!resp.ok) {
      throw new Error(`api_${resp.status}`)
    }
    return (await resp.json()) as T
  } finally {
    clearTimeout(id)
  }
}

export async function sizeByWorkload(input: SizeByWorkloadInput): Promise<PublicSizeResponse> {
  return postWithTimeout<PublicSizeResponse>('/api/public/iaas/size', input)
}

export async function sizeBySpend(input: SizeBySpendInput): Promise<PublicSizeResponse & {
  currentSpend?: { monthly: number; annualized: number; provider: string }
  savingsEstimate?: {
    monthlyLow: number
    monthlyHigh: number
    savingsPctLow: number
    savingsPctHigh: number
  }
}> {
  return postWithTimeout('/api/public/iaas/size-from-spend', input)
}

export async function submitLead(input: LeadInput): Promise<LeadResponse> {
  try {
    const resp = await fetch(`${API_BASE}/api/public/lead`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    return (await resp.json()) as LeadResponse
  } catch {
    return {
      success: false,
      error: 'Something went wrong. Email sales@cylogic.com directly.',
    }
  }
}
